import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { App as AntApp, Row, Col, Form, Input, Button, Typography, Space } from 'antd';
import { userManagementApi } from '../config/api';
import { Encrypt } from '../utils/encryption';
import forgottenbrain from '../assets/Ziniz-Icon-EPS.svg';
import authStyles from '../styles/authShared.module.css';
import resetStyles from '../styles/resetPassword.module.css';
import axios from 'axios';
import {
  PrefixFormItem,
  mobileForApi,
  isMobileLengthValid,
  MOBILE_MAX_LEN,
  mobileFieldInputPlain,
  userNameDisplayFromPrefix,
} from '../constants/CountryCodes';

const { Title } = Typography;

const ResetPassword = () => {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const mobileWatch = Form.useWatch('mobile', form);

  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [finalLoading, setFinalLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [mobileOtp, setMobileOtp] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);

  const mobileInputRef = useRef(null);

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  useEffect(() => {
    setMobileOtp('');
    setShowCodeInput(false);
    setVerificationToken(null);
    setIsMobileVerified(false);
    form.setFieldsValue({ userName: '' });
  }, [mobileWatch, form]);

  const handleVerifyClick = async (action = '') => {
    const prefix = form.getFieldValue('prefix');
    const mobileVal = form.getFieldValue('mobile');

    if (isMobileVerified && action !== 'Resend') {
      setIsMobileVerified(false);
      setShowCodeInput(false);
      setVerificationToken(null);
      setMobileOtp('');
      form.setFieldsValue({ userName: '' });
      return;
    }
    if (action === 'Resend') {
      setMobileOtp('');
    }

    if (!isMobileLengthValid(mobileVal)) return;

    const isResend = action === 'Resend';
    if (isResend) setResendLoading(true);
    else setLoading(true);

    try {
      const response = await axios.post(
        userManagementApi,
        Encrypt({
          action: 'verifyMobile',
          mobile: mobileForApi(prefix, mobileVal),
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      const token = response.data?.token;

      if (token) {
        setShowCodeInput(true);
        setVerificationToken(token);
        setIsMobileVerified(true);
        form.setFieldsValue({
          userName: userNameDisplayFromPrefix(prefix, mobileVal),
        });
        setResendCountdown(46);
        message.success('Verification code sent.');
      }
    } catch (error) {
      const errText =
        error?.response?.data?.message ||
        'Unexpected error occurred. Please try again later.';
      form.setFields([{ name: 'mobile', errors: [errText] }]);
      message.error(errText);
      console.error('Token generation failed:', error);
    } finally {
      if (isResend) setResendLoading(false);
      else setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setFinalLoading(true);

    const payload = {
      action: 'updatePassword',
      token: verificationToken,
      userName: mobileForApi(values.prefix, values.mobile),
      password: values.password,
      confirmPassword: values.confirmPassword,
      mobileVerificationCode: mobileOtp,
      emailVerificationCode: null,
      ...values
    };

    try {
      const response = await axios.post(userManagementApi, Encrypt(payload), {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const responseText =
        typeof response.data?.message === 'string'
          ? response.data.message
          : '';
      if (
        response.status === 200 &&
        response.data?.message?.toLowerCase().includes('success')
      ) {
        message.success(responseText || 'Password updated successfully.');
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 1600);
      } else {
        message.error(
          responseText || 'Could not reset password. Please try again.'
        );
      }
    } catch (error) {
      console.error('Reset error:', error);
      const errText =
        error.response?.data?.message ||
        'Something is not right. Please try again.';
      message.error(String(errText));
    } finally {
      setFinalLoading(false);
    }
  };

  const isFormValid = () => {
    const values = form.getFieldsValue(['userName', 'password', 'confirmPassword']);
    return (
      values.userName &&
      verificationToken &&
      mobileOtp.length > 0 &&
      values.password &&
      values.confirmPassword
    );
  };

  return (
    <div className={authStyles.authPageShell}>
    <div className={resetStyles.resetContainer}>
      <div className={resetStyles.resetLogoContainer}>
        <img
          src={forgottenbrain}
          alt="MindBank Logo"
          className={resetStyles.resetBrainLogo}
        />
      </div>
      <Title level={2}>Password Reset</Title>

      <Form
        form={form}
        name="resetPassword"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        className={`${authStyles.registerForm} ${resetStyles.resetSurfaceForm}`}
        initialValues={{ prefix: '+91' }}
      >
        <Form.Item label="Registered Mobile" required>
          <div className={authStyles.verifyRow}>
            <div className={authStyles.verifyRowFields}>
              <Space.Compact style={{ width: '100%' }}>
                <PrefixFormItem disabled={isMobileVerified} />
                <Form.Item
                  name="mobile"
                  noStyle
                  rules={[
                    { required: true, message: 'Please enter your mobile number' },
                    {
                      validator: (_, value) => {
                        if (!value || !isMobileLengthValid(value)) {
                          return Promise.reject(new Error('Enter 6–14 digits'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <Input
                    ref={mobileInputRef}
                    disabled={isMobileVerified}
                    className={authStyles.emailInput}
                    maxLength={MOBILE_MAX_LEN}
                    style={mobileFieldInputPlain}
                    onChange={(e) => {
                      const v = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, MOBILE_MAX_LEN);
                      form.setFieldsValue({ mobile: v });
                    }}
                  />
                </Form.Item>
              </Space.Compact>
            </div>
            <Button
              type="primary"
              className={authStyles.verifyRowVerifyBtn}
              onClick={() => handleVerifyClick('Edit')}
              disabled={loading || resendLoading || !isMobileLengthValid(mobileWatch)}
              loading={loading}
            >
              {loading
                ? 'Verifying...'
                : isMobileVerified
                  ? 'Edit'
                  : 'Verify'}
            </Button>
          </div>

          {showCodeInput && (
            <div className={authStyles.inlineVerificationBlock}>
              <div className={authStyles.inlineVerificationOtp}>
                <span className={authStyles.inlineVerificationLabel}>SMS / WhatsApp code</span>
                <Input.OTP
                  length={6}
                  value={mobileOtp}
                  onChange={setMobileOtp}
                  disabled={resendLoading}
                />
              </div>
              <Button
                type="default"
                size="small"
                className={authStyles.inlineVerificationResend}
                onClick={() => handleVerifyClick('Resend')}
                disabled={resendCountdown > 0 || resendLoading}
                loading={resendLoading}
              >
                {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend code'}
              </Button>
            </div>
          )}
        </Form.Item>

        <Form.Item
          label="User Name (Auto-populated)"
          name="userName"
          rules={[{ required: true, message: 'User name is required' }]}
        >
          <Input disabled />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="New Password"
              name="password"
              rules={[
                { required: true, message: 'Please enter a password' },
                { min: 5, message: 'Must be 5 characters or more' },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <div className={authStyles.formActionsFullWidth}>
            <Button
              type="default"
              onClick={() => navigate('/login')}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={finalLoading}
              disabled={!isFormValid()}
            >
              {finalLoading ? 'Changing Password...' : 'Reset Password'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
    </div>
  );
};

export default ResetPassword;
